import { useMemo, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { TextField } from '@mui/material';
import styled from 'styled-components';

import { setPblCitizenFirstPassword } from '@/api/pbl.api';
import AuthContainer from '@/components/Container/AuthContainer';
import { Button } from '@/components/UI/Button';
import { Visible } from '@/components/Icon';
import { Text } from '@/components/UI/Typography/Text';
import { getAuthSelector, signIn } from '@/redux/auth/auth.slice';
import { useAppDispatch, useAppSelector } from '@/redux/store.hook';
import PATH from '@/router/path';
import CryptoJS from 'crypto-js';
import { getAuthRefreshDeadline } from '@/utils/authToken';
import { hasPblRole } from '@/utils/pblRole';

import { isValidPblPassword } from './passwordRules';
import { Box } from '@/layout/Box';
import { useTranslation } from 'react-i18next';

type PasswordSetupForm = {
  password: string;
  passwordConfirmation: string;
};

const Form = styled.form`
  display: flex;
  flex-direction: column;
  flex-grow: 1;
`;

const IntroText = styled(Text)`
  margin: 0 0 44px;
`;

const FieldStack = styled.div`
  display: grid;
  gap: 24px;
  margin-bottom: 34px;
`;

const PasswordFieldWrapper = styled.div`
  position: relative;
`;

const VisibilityButton = styled.button`
  position: absolute;
  right: 0;
  top: 12px;
  display: inline-flex;
  padding: 0;
  border: 0;
  background: transparent;
  color: ${(p) => p.theme.colors.gray500};
  cursor: pointer;

  svg {
    width: 28px;
    height: 28px;
  }
`;

const ErrorText = styled(Text)`
  margin: 0 0 20px;
  color: ${(p) => p.theme.colors.danger};
  font-size: ${(p) => p.theme.fontSizes.sm};
`;

const resolveRedirectPath = (redirectTo?: string | null) => {
  if (!redirectTo) return '';

  try {
    const url = new URL(redirectTo, window.location.origin);
    if (url.origin === window.location.origin) {
      return `${url.pathname}${url.search}${url.hash}`;
    }

    return redirectTo;
  } catch {
    if (redirectTo.startsWith('/')) return redirectTo;
  }

  return '';
};

const PasswordSetup = () => {
  //   @ts-ignore
  const { t } = useTranslation();
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const auth = useAppSelector(getAuthSelector);
  const [searchParams] = useSearchParams();
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showPasswordConfirmation, setShowPasswordConfirmation] = useState(false);
  const {
    control,
    handleSubmit,
    watch,
    formState: { isDirty },
  } = useForm<PasswordSetupForm>({
    mode: 'onChange',
    defaultValues: {
      password: '',
      passwordConfirmation: '',
    },
  });

  const redirectTo = useMemo(() => resolveRedirectPath(searchParams.get('redirect_to')), [searchParams]);
  const password = watch('password');
  const passwordConfirmation = watch('passwordConfirmation');
  const canSubmit = isDirty && isValidPblPassword(password) && password === passwordConfirmation && !loading;

  const submit = handleSubmit(async (data) => {
    setError('');

    if (!auth.isLogin || !auth.accessToken) {
      setError('Please open the secure login link again before setting your password.');
      return;
    }

    if (!isValidPblPassword(data.password)) {
      setError('Password must be at least 9 characters and include uppercase, lowercase, number and symbol.');
      return;
    }

    if (data.password !== data.passwordConfirmation) {
      setError('Password confirmation does not match.');
      return;
    }

    setLoading(true);

    try {
      const res = (
        await setPblCitizenFirstPassword({
          password: CryptoJS.MD5(data.password).toString(),
          password_confirmation: CryptoJS.MD5(data.passwordConfirmation).toString(),
        })
      ).data;

      if (!res.success) {
        setError(res.message || 'Unable to set password.');
        return;
      }

      dispatch(
        signIn({
          isLogin: true,
          accessToken: res.data.access_token,
          userData: res.data.user,
          applications: res.data.applications,
          registDone: true,
          refreshTokenTime: getAuthRefreshDeadline(res.data.expires_in),
        })
      );

      const defaultPath = hasPblRole(res.data.user || auth.userData, 'citizen')
        ? PATH.pbl.citizenCaseCreate
        : PATH.pbl.cases;
      const nextPath = resolveRedirectPath(res.data.passwordless_redirect_to || redirectTo) || defaultPath;

      if (nextPath.startsWith('/')) {
        navigate(nextPath, { replace: true });
        return;
      }

      window.location.href = nextPath;
    } catch (err: any) {
      setError(err?.response?.data?.message || 'Unable to set password.');
    } finally {
      setLoading(false);
    }
  });

  return (
    <AuthContainer title={t('pbl.pagesHardcoded.passwordSetup.setPassword')}>
      <Form onSubmit={submit}>
        <IntroText>{t('pbl.pagesHardcoded.passwordSetup.setPasswordAndLogInAgain')}</IntroText>
        <FieldStack>
          <PasswordFieldWrapper>
            <Controller
              control={control}
              name="password"
              rules={{
                required: t('pbl.pagesHardcoded.passwordSetup.passwordIsRequired'),
                validate: (value) =>
                  isValidPblPassword(value) ||
                  t('pbl.pagesHardcoded.passwordSetup.atLeast9CharactersIncluding1UppercaseLetter'),
              }}
              render={({ field, fieldState: { error: fieldError } }) => (
                <TextField
                  {...field}
                  label={t('pbl.pagesHardcoded.passwordSetup.password')}
                  variant="standard"
                  type={showPassword ? 'text' : 'password'}
                  fullWidth
                  error={!!fieldError}
                  helperText={
                    fieldError?.message ||
                    t('pbl.pagesHardcoded.passwordSetup.atLeast9CharactersIncluding1UppercaseLetter')
                  }
                  autoComplete="new-password"
                />
              )}
            />
            <VisibilityButton
              type="button"
              aria-label={
                showPassword
                  ? t('pbl.pagesHardcoded.passwordSetup.hidePassword')
                  : t('pbl.pagesHardcoded.passwordSetup.showPassword')
              }
              onClick={() => setShowPassword((value) => !value)}
            >
              {showPassword ? <Visible.VisibleSVG /> : <Visible.VisibleOffSVG />}
            </VisibilityButton>
          </PasswordFieldWrapper>
          <PasswordFieldWrapper>
            <Controller
              control={control}
              name="passwordConfirmation"
              rules={{
                required: t('pbl.pagesHardcoded.passwordSetup.pleaseReEnterYourPassword'),
                validate: (value) =>
                  value === password || t('pbl.pagesHardcoded.passwordSetup.pleaseEnsureBothPasswordsMatch'),
              }}
              render={({ field, fieldState: { error: fieldError } }) => (
                <TextField
                  {...field}
                  label={t('pbl.pagesHardcoded.passwordSetup.reEnterPassword')}
                  variant="standard"
                  type={showPasswordConfirmation ? 'text' : 'password'}
                  fullWidth
                  error={!!fieldError}
                  helperText={
                    fieldError?.message || t('pbl.pagesHardcoded.passwordSetup.pleaseEnsureBothPasswordsMatch')
                  }
                  autoComplete="new-password"
                />
              )}
            />
            <VisibilityButton
              type="button"
              aria-label={
                showPasswordConfirmation
                  ? t('pbl.pagesHardcoded.passwordSetup.hideReEnteredPassword')
                  : t('pbl.pagesHardcoded.passwordSetup.showReEnteredPassword')
              }
              onClick={() => setShowPasswordConfirmation((value) => !value)}
            >
              {showPasswordConfirmation ? <Visible.VisibleSVG /> : <Visible.VisibleOffSVG />}
            </VisibilityButton>
          </PasswordFieldWrapper>
        </FieldStack>
        {error && <ErrorText>{error}</ErrorText>}
        <Box>
          <Button size="lg" variant="primary" rounded={false} type="submit" disabled={!canSubmit}>
            {loading ? t('pbl.pagesHardcoded.passwordSetup.saving') : t('pbl.pagesHardcoded.passwordSetup.next')}
          </Button>
        </Box>
      </Form>
    </AuthContainer>
  );
};

export default PasswordSetup;
